import { Divider, Group, Select } from '@byecode/ui'
import type { ScreenShotAction } from '@lighthouse/core'
import type { FlowNode, NodeTypes } from '@lighthouse/shared'
import { ListItem4ByecodeUi, nodeTypeOptions, scroll2FlowNode, useAtomAction, useAtomData } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { pageSyncComponentsAtom } from '@/atoms/application/state'
import { blockHighlightAtom, lastPageOfStackAtom, pageBlocksAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'

import { ComponentSelector } from '../../Common/ComponentSelector'
import * as SC from '../../styles'
import { generateOptionsWithSync } from '../ScrollToActionConfigure'

interface ScreenShotActionConfigureProps {
    allParentNodes?: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    nodeType?: NodeTypes
    value: ScreenShotAction['data']['screenShot']
    onChange: (value: ScreenShotAction['data']['screenShot']) => void
}

const SCALE_OPTIONS = [
    {
        label: '0.5x',
        value: '0.5'
    },
    {
        label: '0.75x',
        value: '0.75'
    },
    {
        label: '1x',
        value: '1'
    },
    {
        label: '1.5x',
        value: '1.5'
    },
    {
        label: '2x',
        value: '2'
    },
    {
        label: '3x',
        value: '3'
    },
    {
        label: '4x',
        value: '4'
    },
    {
        label: '5x',
        value: '5'
    },
    {
        label: '6x',
        value: '6'
    },
    {
        label: '7x',
        value: '7'
    },
    {
        label: '8x',
        value: '8'
    },
    {
        label: '9x',
        value: '9'
    },
    {
        label: '10x',
        value: '10'
    }
]

export const ScreenShotActionConfigure: React.FC<ScreenShotActionConfigureProps> = ({ actionTypeSwitcher, nodeType, value, onChange }) => {
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const { pageId, stackId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => ({ pageId: s?.pageId || '', stackId: s?.stackId || '' }), [])
    )
    const selectedNodes = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.selectedNodes, [])
    )

    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const syncComponents = useAtomData(pageSyncComponentsAtom)

    const options = useMemo(() => {
        const currentNode = selectedNodes?.[0]
        if (!currentNode) {
            return []
        }

        return generateOptionsWithSync(currentNode, pageBlocks, syncComponents) ?? []
    }, [pageBlocks, selectedNodes, syncComponents])

    const handleScroll2FlowNode = useCallback(
        (value?: NodeIdWithScope) => {
            setBlockHighlight(value ? [value.id] : [])
            value && scroll2FlowNode(value.id, value.scope)
        },
        [setBlockHighlight]
    )

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            <Divider />

            <Group label="生成对象">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>选择组件</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <ComponentSelector
                            value={value.screenShotElementId || ''}
                            onChange={node => onChange({ ...value, screenShotElementId: node.id })}
                            options={options}
                            onHover={handleScroll2FlowNode}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>图片倍数</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select value={value.scale} onChange={scale => onChange({ ...value, scale })} options={SCALE_OPTIONS} />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
